import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Génère un PDF sobre et institutionnel des résultats d'éligibilité
 * Structure : Synthèse, Détail des aides, Accompagnement
 */
export function generateResultsPDF(lead, detailedResults) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPos = 20;

  // Couleurs sobres
  const colors = {
    primary: [26, 77, 46],      // Vert foncé #1A4D2E
    secondary: [100, 100, 100], // Gris
    text: [30, 30, 30],         // Noir
    light: [150, 150, 150],     // Gris clair
  };

  // === EN-TÊTE ===
  doc.setFontSize(18);
  doc.setTextColor(...colors.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Synthèse de votre éligibilité aux aides', margin, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  doc.setTextColor(...colors.light);
  doc.setFont('helvetica', 'normal');
  doc.text('Rénovation énergétique - Document informatif', margin, yPos);

  // Date de génération
  const today = new Date();
  const dateStr = today.toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
  doc.text(`Généré le ${dateStr}`, pageWidth - margin - 50, yPos);

  yPos += 5;
  doc.setDrawColor(...colors.light);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // === PARTIE 1 : SYNTHÈSE ===
  doc.setFontSize(14);
  doc.setTextColor(...colors.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('1. Synthèse de votre situation', margin, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setTextColor(...colors.text);
  doc.setFont('helvetica', 'normal');

  // Informations du demandeur
  const leadData = detailedResults?.lead_data || lead || {};
  const beneficiaryType = lead?.beneficiary_type || 'particulier';
  
  let demandeurInfo = [];
  if (beneficiaryType === 'particulier') {
    demandeurInfo = [
      `Demandeur : ${lead?.first_name || ''} ${lead?.last_name || ''}`,
      `Adresse : ${lead?.address || ''}, ${lead?.postal_code || ''} ${lead?.city || ''}`,
      `Type de logement : ${lead?.housing_type === 'maison' ? 'Maison individuelle' : 'Appartement'}`,
      `Surface : ${lead?.housing_surface || '—'} m²`,
      `Ancienneté : ${lead?.housing_age || '—'} ans`
    ];
  } else if (beneficiaryType === 'copropriete') {
    demandeurInfo = [
      `Copropriété : ${lead?.copro_name || ''}`,
      `Adresse : ${lead?.address || lead?.copro_address || ''}, ${lead?.postal_code || ''} ${lead?.city || ''}`,
      `Nombre de lots : ${lead?.total_lots || '—'}`,
      `Résidences principales : ${lead?.residence_principale_ratio || '—'}%`,
      `Syndic : ${lead?.syndic_name || '—'}`
    ];
  }

  demandeurInfo.forEach(line => {
    doc.text(line, margin, yPos);
    yPos += 5;
  });

  yPos += 5;

  // Encadré synthèse des aides
  const totalAid = detailedResults?.total_estimated_aid || 0;
  const totalCost = detailedResults?.total_estimated_cost || 0;
  const remaining = detailedResults?.total_remaining_cost || 0;

  doc.setFillColor(245, 245, 245);
  doc.roundedRect(margin, yPos, contentWidth, 25, 2, 2, 'F');
  
  yPos += 8;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Estimation des aides mobilisables', margin + 5, yPos);
  
  yPos += 7;
  doc.setFontSize(16);
  doc.setTextColor(...colors.primary);
  doc.text(`${totalAid.toLocaleString('fr-FR')} €`, margin + 5, yPos);
  
  doc.setFontSize(10);
  doc.setTextColor(...colors.secondary);
  doc.setFont('helvetica', 'normal');
  if (totalCost > 0) {
    doc.text(`Coût estimé des travaux : ${totalCost.toLocaleString('fr-FR')} €`, margin + 80, yPos - 5);
    doc.text(`Reste à charge estimé : ${remaining.toLocaleString('fr-FR')} €`, margin + 80, yPos + 2);
  }

  yPos += 20;

  // === PARTIE 2 : DÉTAIL DES AIDES ===
  doc.setFontSize(14);
  doc.setTextColor(...colors.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('2. Détail des aides identifiées', margin, yPos);
  yPos += 10;

  // Tableau des aides
  const programs = detailedResults?.official_programs || [];
  const tableData = programs
    .filter(p => p.estimated_amount > 0 || p.eligibility_status === 'eligible' || p.eligibility_status === 'maybe')
    .map(program => {
      let statusText = '';
      switch (program.eligibility_status) {
        case 'eligible':
          statusText = 'Éligible';
          break;
        case 'maybe':
          statusText = 'À confirmer';
          break;
        case 'not_eligible':
          statusText = 'Non éligible';
          break;
        default:
          statusText = '—';
      }
      
      return [
        program.name || program.program_name,
        program.payer || '—',
        statusText,
        program.estimated_amount > 0 ? `${program.estimated_amount.toLocaleString('fr-FR')} €` : '—'
      ];
    });

  if (tableData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['Dispositif', 'Financeur', 'Statut', 'Montant estimé']],
      body: tableData,
      theme: 'plain',
      headStyles: {
        fillColor: [26, 77, 46],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [30, 30, 30]
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248]
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 35 },
        2: { cellWidth: 30 },
        3: { cellWidth: 35, halign: 'right' }
      },
      margin: { left: margin, right: margin }
    });

    yPos = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(10);
    doc.setTextColor(...colors.secondary);
    doc.text('Aucune aide identifiée pour le moment.', margin, yPos);
    yPos += 10;
  }

  // Vérifier si on doit passer à une nouvelle page
  if (yPos > 240) {
    doc.addPage();
    yPos = 20;
  }

  // Sources réglementaires
  yPos += 5;
  doc.setFontSize(8);
  doc.setTextColor(...colors.light);
  doc.setFont('helvetica', 'italic');
  doc.text('Sources : ANAH, Service-Public.fr, France Rénov\' - Barèmes en vigueur au ' + dateStr, margin, yPos);
  
  yPos += 15;

  // === PARTIE 3 : ACCOMPAGNEMENT ===
  // Vérifier si on doit passer à une nouvelle page
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(...colors.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('3. Prochaines étapes et accompagnement', margin, yPos);
  yPos += 10;

  doc.setFontSize(10);
  doc.setTextColor(...colors.text);
  doc.setFont('helvetica', 'normal');

  const nextSteps = [
    '1. Validation de votre éligibilité par un conseiller agréé',
    '2. Obtention de devis auprès d\'artisans certifiés RGE',
    '3. Constitution du dossier de demande d\'aides',
    '4. Réalisation des travaux après accord de financement',
    '5. Versement des aides après contrôle des travaux'
  ];

  nextSteps.forEach(step => {
    doc.text(step, margin, yPos);
    yPos += 6;
  });

  yPos += 8;

  // Encadré d'accompagnement
  doc.setFillColor(245, 250, 247);
  doc.setDrawColor(...colors.primary);
  doc.roundedRect(margin, yPos, contentWidth, 35, 2, 2, 'FD');

  yPos += 8;
  doc.setFont('helvetica', 'bold');
  doc.text('Besoin d\'accompagnement ?', margin + 5, yPos);
  
  yPos += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const accompText = [
    'France Rénov\' : 0 808 800 700 (service public gratuit)',
    'Espace Conseil : www.france-renov.gouv.fr',
    'ANAH : www.anah.gouv.fr'
  ];
  accompText.forEach(line => {
    doc.text(line, margin + 5, yPos);
    yPos += 5;
  });

  yPos += 15;

  // === PIED DE PAGE ===
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Ligne de séparation
    doc.setDrawColor(...colors.light);
    doc.line(margin, 280, pageWidth - margin, 280);
    
    // Mentions légales
    doc.setFontSize(7);
    doc.setTextColor(...colors.light);
    doc.setFont('helvetica', 'normal');
    doc.text(
      'Document à caractère informatif. Les montants indiqués sont des estimations basées sur les barèmes en vigueur.',
      margin, 285
    );
    doc.text(
      'L\'éligibilité définitive est soumise à la validation des organismes financeurs. Ce document ne constitue pas un engagement.',
      margin, 289
    );
    
    // Numéro de page
    doc.text(`Page ${i}/${pageCount}`, pageWidth - margin - 15, 289);
  }

  // Nom du fichier
  const fileName = `eligibilite_aides_${lead?.last_name || lead?.copro_name || 'resultat'}_${today.toISOString().split('T')[0]}.pdf`;
  
  return { doc, fileName };
}

/**
 * Télécharge le PDF généré
 */
export function downloadResultsPDF(lead, detailedResults) {
  try {
    const { doc, fileName } = generateResultsPDF(lead, detailedResults);
    doc.save(fileName);
    return true;
  } catch (error) {
    console.error('Erreur génération PDF:', error);
    return false;
  }
}
